import axios from "axios";
import headers from "../../helper/requestHeader/RequestHeader";
import {
    CreateTripForWebRequest, CreateUETDSTripDTO, DriverTripStatusHistoryItem, RoundTripPageData, StartPassengerTripRequestDTO,
    TripDetailDTO,
    TripModel, TripOperationStatusEnum,
    TripPageData,
    TripSearchDTO, UETDSTripDetail, UETDSTripPageData
} from "../../model/trip/TripModel";
import {PassengerCallPageData, VehicleCallPageData} from "../../model/passengerCall/PassengerCallModel";
import {VehicleTypeEnum} from "../../model/vehicle/VehicleModel";
import {InvoiceEventPaymentEnum} from "../../model/invoiceEvent/InvoiceEventModel";
import {ResultModel} from "../../model/base/ResultModel";
import {UserModel} from "../../model/user/UserModel";
import {
    CancelPassengerTripRequestDTO,
    PassengerTripDetailDTO,
    PassengerTripListItemDTO,
    TripPassengerDTO
} from "../../model/user/PassengerModel";
import config from "../../../config/env/config";
import {runLatestRequest} from "../../helper/latestRequest";


const API_URL = config.baseApiUrl;
const AGENT_URL = config.agentApiUrl;

const search = async (
    tripSearch: TripSearchDTO | undefined,
    page: number = 0,
    pageSize: number = 25,
    orderProperty?: string | number
    , orderType?: string): Promise<TripPageData> => {
    return runLatestRequest("trip-search", (signal) =>
        axios.post<TripPageData>(
            `${API_URL}/api/trip/search`,
            tripSearch,
            {
                headers: headers,
                params: {
                    page: page,
                    pageSize: pageSize,
                    orderProperty: orderProperty,
                    orderType: orderType
                },
                signal
            }
        ).then((response) => response.data)
    );
}

const searchFirmTrips = async (
    tripSearch: TripSearchDTO | undefined,
    page: number = 0,
    pageSize: number = 25,
    orderProperty?: string | number
    , orderType?: string): Promise<TripPageData> => {
    return runLatestRequest("trip-firm-search", (signal) =>
        axios.post<TripPageData>(
            `${API_URL}/api/trip/firm/search`,
            tripSearch,
            {
                headers: headers,
                params: {
                    page: page,
                    pageSize: pageSize,
                    orderProperty: orderProperty,
                    orderType: orderType
                },
                signal
            }
        ).then((response) => response.data)
    );
}

const searchRoundTrip = async (
    tripSearch: TripSearchDTO | undefined,
    page: number = 0,
    pageSize: number = 25,
    orderProperty?: string | number
    , orderType?: string): Promise<RoundTripPageData> => {
    const response = await axios.post<RoundTripPageData>(
        `${API_URL}/api/trip/round-search`,
        tripSearch,
        {
            headers: headers,
            params: {
                page: page,
                pageSize: pageSize,
                orderProperty: orderProperty,
                orderType: orderType
            }
        }
    );

    return response.data;
}

const searchUETDSTrip = async (
    tripSearch: TripSearchDTO | undefined,
    page: number = 0,
    pageSize: number = 25,
    orderProperty?: string | number
    , orderType?: string): Promise<UETDSTripPageData> => {
    return runLatestRequest("trip-uetds-search", (signal) =>
        axios.post<UETDSTripPageData>(
            `${API_URL}/api/trip/uetds-search`,
            tripSearch,
            {
                headers: headers,
                params: {
                    page: page,
                    pageSize: pageSize,
                    orderProperty: orderProperty,
                    orderType: orderType
                },
                signal
            }
        ).then((response) => response.data)
    );
}

const searchPassengerCallTrip = async (
    passengerCallStatus: number | undefined,
    vehicleType: VehicleTypeEnum | undefined,
    passengerId: string | null | undefined
    , page: number = 0, pageSize: number = 25, orderProperty?: string | number, orderType?: string): Promise<PassengerCallPageData> => {
    const response = await axios.post<PassengerCallPageData>(
        `${API_URL}/api/trip/passenger/call-search`,
        null,
        {
            headers: headers,
            params: {
                passengerCallStatus: passengerCallStatus,
                vehicleType: vehicleType,
                passengerId: passengerId,
                page: page,
                pageSize: pageSize,
                orderProperty: orderProperty,
                orderType: orderType
            }
        }
    );

    return response.data;
}

const searchVehicleCallTrip = async (
    vehicleCallStatus: number | undefined,
    vehicleType: VehicleTypeEnum | undefined,
    driverId: string | null | undefined
    , page: number = 0, pageSize: number = 25, orderProperty?: string | number, orderType?: string): Promise<VehicleCallPageData> => {
    const response = await axios.post<VehicleCallPageData>(
        `${API_URL}/api/trip/vehicle/call-search`,
        null,
        {
            headers: headers,
            params: {
                vehicleCallStatus: vehicleCallStatus,
                vehicleType: vehicleType,
                driverId: driverId,
                page: page,
                pageSize: pageSize,
                orderProperty: orderProperty,
                orderType: orderType
            }
        }
    );

    return response.data;
}

const getAllAgentTrips = async (
    agentId: number | undefined,
    agentPaymentStatus: InvoiceEventPaymentEnum | undefined,
    startDate: Date | string | null,
    endDate: Date | string | null,
): Promise<TripModel[]> => {
    return runLatestRequest("trip-agent-all", (signal) =>
        axios.get<TripModel[]>(
            `${API_URL}/api/trip/agent/all`,
            {
                headers: headers,
                params: {
                    agentId: agentId,
                    agentPaymentStatus: agentPaymentStatus,
                    startDate: startDate,
                    endDate: endDate,
                },
                signal
            }
        ).then((response) => response.data)
    );
}

const searchTripComments = async (
    tripId: number | undefined,
    comment: string | undefined,
    driverId: string | null | undefined,
    page: number = 0,
    pageSize: number = 25
    , orderProperty?: string | number, orderType?: string): Promise<TripPageData> => {
    const response = await axios.post<TripPageData>(
        `${API_URL}/api/trip/comment/search`,
        null,
        {
            headers: headers,
            params: {
                tripId: tripId,
                comment: comment,
                driverId: driverId,
                page: page,
                pageSize: pageSize,
                orderProperty: orderProperty,
                orderType: orderType
            }
        }
    );

    return response.data;
}

const findById = async (tripId: string | number | undefined): Promise<TripDetailDTO> => {
    const response = await axios.get<TripDetailDTO>(
        `${API_URL}/api/trip/${tripId}`,
        {
            headers: headers
        }
    );

    return response.data;
};

/**
 * Surucu yolculugunun altinda toplanan yolcu paylasimli yolculuklari getirir.
 * Surucu yolculugu detayindaki "Yolcu Paylasimli Yolculuklar" sekmesi kullanir.
 */
const findSharedPassengerTrips = async (tripId: string | number | undefined): Promise<TripModel[]> => {
    const response = await axios.get<TripModel[]>(
        `${API_URL}/api/trip/${tripId}/shared-passenger-trips`,
        {
            headers: headers
        }
    );

    return response.data;
};

/**
 * Yolculugun surucu statu gecmisini (eskiden yeniye) getirir. Surucu/arac degisse de
 * eski kayitlar doner; her kayit o an atanmis araci ve aracin konumunu tasir.
 */
const findDriverTripStatusHistory = async (tripId: string | number | undefined): Promise<DriverTripStatusHistoryItem[]> => {
    const response = await axios.get<ResultModel<any>>(
        `${API_URL}/api/driver-trip-status/history`,
        {
            headers: headers,
            params: {tripId}
        }
    );

    if (response.data?.status !== "200") {
        throw new Error(response.data?.detailMessage || "Sürücü statü geçmişi alınamadı.");
    }
    return (response.data.data?.["history"] ?? []) as DriverTripStatusHistoryItem[];
};

const findFirmTripById = async (tripId: string | number | undefined): Promise<TripDetailDTO> => {
    const response = await axios.get<TripDetailDTO>(
        `${API_URL}/api/trip/firm/${tripId}`,
        {
            headers: headers
        }
    );

    return response.data;
};

const getAgentTripById = async (tripId: string | number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.get<ResultModel<any>>(
        `${AGENT_URL}/api/v1/agent/trip/getTrip/${tripId}`,
        {
            headers: headers
        }
    );

    return response.data;
};


const findAllByRequestId = async (requestId: number): Promise<TripModel[]> => {
    const response = await axios.get<TripModel[]>(
        `${API_URL}/api/trip/all-round/${requestId}`,
        {
            headers: headers
        }
    );

    return response.data;
};

const approvedOfTripPayment = async (tripId: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/approvedOfTripPayment`,
        {trip: tripId},
        {
            headers: headers
        }
    );

    return response.data;
}

const approvedOfAgentPayment = async (tripIds: (number | undefined)[]): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/approvedOfAgentPayment`,
        {tripList: tripIds},
        {
            headers: headers
        }
    );

    return response.data;
}

const createTripForWeb = async (webRequest: CreateTripForWebRequest | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/createTripForWeb`,
        webRequest,
        {
            headers: headers
        }
    );

    return response.data;
}

const createTripForMobile = async (webRequest: CreateTripForWebRequest | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/createTripForMobile`,
        webRequest,
        {
            headers: headers
        }
    );

    return response.data;
}

const createTripForAgent = async (webRequest: CreateTripForWebRequest | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${AGENT_URL}/api/v1/agent/trip/createTrip`,
        webRequest,
        {
            headers: headers
        }
    );

    return response.data;
}

const getTripStatus = async (tripId: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.get<ResultModel<any>>(
        `${API_URL}/trip/status/${tripId}`,
        {
            headers: headers
        }
    );

    return response.data;
};

const cancelTrip = async (tripId: number | undefined, force: boolean | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/cancelTrip`,
        {trip: tripId, force: force},
        {
            headers: headers
        }
    );

    return response.data;
}

const reopenRequest = async (requestId: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/reopenRequest`,
        {requestId: requestId},
        {
            headers: headers
        }
    );

    return response.data;
}

const assignNewDriver = async (tripId: number | undefined, firmId: number | undefined, driverId: number | undefined, vehicleId: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/assignNewDriverAgentTripForFirm`,
        {tripId: tripId, firmId: firmId, driverId: driverId, vehicleId},
        {
            headers: headers
        }
    );

    return response.data;
}

const updateDriverAmountForTrip = async (tripId: number | undefined, newAmount: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateDriverAmountForTrip`,
        {tripId: tripId, newAmount: newAmount},
        {
            headers: headers
        }
    );

    return response.data;
}

const updateZuppinCommissionForAgentRequest = async (requestId: number | undefined, commission: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateZuppinCommissionForAgentRequest`,
        {requestId: requestId, commission: commission},
        {
            headers: headers
        }
    );

    return response.data;
}

const updateTripStartTime = async (tripId: number | undefined, startTime: number | Date | null | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateTripStartTime`,
        {tripId: tripId, startTime: startTime},
        {
            headers: headers
        }
    );

    return response.data;
}

const addTripPassenger = async (tripId: number | undefined, passenger: UserModel): Promise<UserModel[]> => {
    const response = await axios.post<UserModel[]>(
        `${API_URL}/api/trip/passenger/add/${tripId}`,
        passenger,
        {
            headers: headers
        }
    );

    return response.data;
}

const updateTripPassenger = async (passenger: UserModel): Promise<UserModel[]> => {
    const response = await axios.post<UserModel[]>(
        `${API_URL}/api/trip/passenger/update`,
        passenger,
        {
            headers: headers
        }
    );

    return response.data;
}

const deleteTripPassenger = async (tripPassengerId: number | undefined): Promise<UserModel[]> => {
    const response = await axios.delete<UserModel[]>(
        `${API_URL}/api/trip/passenger/delete/${tripPassengerId}`,
        {
            headers: headers
        }
    );

    return response.data;
}

const cancelAgentTrip = async (tripId: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${AGENT_URL}/api/v1/agent/trip/cancelTrip`,
        {tripId: tripId},
        {
            headers: headers
        }
    );

    return response.data;
}

const createUETDSTrip = async (createUTDSTripDTO: CreateUETDSTripDTO): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/createUetdsTrip`,
        createUTDSTripDTO,
        {
            headers: headers
        }
    );
    return response.data;
}

const cancelUETDSTrip = async (tripId: number): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/cancelUetdsTrip`,
        {uetdsTrip: tripId},
        {
            headers: headers
        }
    );
    return response.data;
}

const findUETDSTripById = async (tripId: string | number | undefined): Promise<UETDSTripDetail> => {
    const response = await axios.get<UETDSTripDetail>(
        `${API_URL}/api/uetds/${tripId}`,
        {
            headers: headers
        }
    );

    return response.data;
};

const getUetdsDetailDocument = async (tripId: number | undefined, uetdsTripId: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/getUetdsDetailDocument`,
        {trip: tripId, uetdsTrip: uetdsTripId},
        {
            headers: headers
        }
    );
    return response.data;
}

const updateUETDSTripPassengers = async (tripId: number | undefined, passengerList: TripPassengerDTO[] | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateUetdsTripPassengers/${tripId}`,
        {passengerList: passengerList},
        {
            headers: headers
        }
    );
    return response.data;
}

const updateTripPrice = async (tripId: number | undefined, newAmount: number | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateTripPrice`,
        {tripId: tripId, newAmount: newAmount},
        {
            headers: headers
        }
    );

    return response.data;
}

const requestsDiscountByCouponCode = async (couponCode: string | undefined, tripOptionsId: number | undefined, vehicleHourlyRentalId: number | undefined, additionalServicesPricesId: number[] | undefined): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/requestsDiscountByCouponCode`,
        {
            couponCode: couponCode,
            tripOptionsId: tripOptionsId,
            vehicleHourlyRentalId: vehicleHourlyRentalId,
            additionalServicesPricesId: additionalServicesPricesId
        },
        {
            headers: headers
        }
    );

    return response.data;
}

type GetTripDetailResponse = {
    trip?: PassengerTripDetailDTO;
    [key: string]: any;
};


export const getTripsOfPassenger = async (): Promise<TripModel[]> => {
    const response = await axios.get<TripModel[]>(
        `${API_URL}/api/getTripsOfPassenger`,
        {
            headers,
        }
    );

    return response.data;
};

export const getPassengerTripDetail = async (
    tripId: number
): Promise<TripDetailDTO | null> => {
    const response = await axios.get<ResultModel<any>>(
        `${API_URL}/api/getTrip/${tripId}`,
        {
            headers,
        }
    );

    return response.data?.data?.trip || null;
};

export const cancelPassengerTrip = async (
    payload: CancelPassengerTripRequestDTO
): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/cancelTrip`,
        payload,
        {
            headers,
        }
    );

    return response.data;
};


export const startPassengerTrip = async (
    payload: StartPassengerTripRequestDTO
): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/startTrip`,
        payload,
        { headers }
    );

    return response.data;
};

export const endPassengerTrip = async (
    tripId: number
): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/endTrip/${tripId}`,
        {},
        { headers }
    );

    return response.data;
};

export const updateFlightNumberForTrip = async (
    tripId: number | undefined,
    flightNumber: string | undefined | null
): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateFlightNumberForTrip`,
        { tripId: tripId, flightNumber: flightNumber },
        {
            headers: headers
        }
    );

    return response.data;
};

export const addNoteForTrip = async (
    tripId: number | undefined,
    note: string | undefined | null
): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/addNoteForTrip`,
        { tripId: tripId, note: note },
        {
            headers: headers
        }
    );

    return response.data;
};

export const updateOperationStatusForTrip = async (
    tripId: number | undefined,
    operationStatus: TripOperationStatusEnum
): Promise<ResultModel<any>> => {
    const response = await axios.post<ResultModel<any>>(
        `${API_URL}/api/updateOperationStatusForTrip`,
        {
            tripId,
            operationStatus
        },
        {
            headers
        }
    );

    return response.data;
};

const tripService = {
    search,
    findById,
    findSharedPassengerTrips,
    findDriverTripStatusHistory,
    searchRoundTrip,
    findAllByRequestId,
    searchUETDSTrip,
    searchPassengerCallTrip,
    searchVehicleCallTrip,
    getAllAgentTrips,
    searchTripComments,
    approvedOfTripPayment,
    approvedOfAgentPayment,
    createTripForWeb,
    getTripStatus,
    cancelTrip,
    reopenRequest,
    assignNewDriver,
    updateDriverAmountForTrip,
    addTripPassenger,
    updateTripPassenger,
    deleteTripPassenger,
    cancelAgentTrip,
    createTripForAgent,
    getAgentTripById,
    createUETDSTrip,
    cancelUETDSTrip,
    findUETDSTripById,
    getUetdsDetailDocument,
    updateUETDSTripPassengers,
    updateTripStartTime,
    updateZuppinCommissionForAgentRequest,
    updateTripPrice,
    requestsDiscountByCouponCode,
    createTripForMobile,
    cancelPassengerTrip,
    getPassengerTripDetail,
    getTripsOfPassenger,
    startPassengerTrip,
    endPassengerTrip,
    addNoteForTrip,
    updateFlightNumberForTrip,
    searchFirmTrips,
    findFirmTripById,
    updateOperationStatusForTrip
};
export default tripService;